import React, { FC, useEffect, useMemo, useState, useContext } from "react";
import { Link } from "react-router-dom";

import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import {
	getSurfaceBorderColor,
	getSurfaceBorderStyle,
} from "@/app/application/theme";
import type {
	ArchiveProject,
	ArchivedProjectsRequest,
	ArchivedProjectsResponse,
} from "@/features/archive/model/archive/ArchivedProjectsRequest";
import { PdfMenu } from "@/features/archive/ui/components/history/PdfMenu";
import StepModal from "@/features/archive/ui/components/history/StepModal";
import ReplayIcon from "@mui/icons-material/Replay";
import TableChartIcon from "@mui/icons-material/TableChart";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
	Box,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Divider,
	IconButton,
	LinearProgress,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Typography,
	alpha,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import dayjs from "dayjs";

import { useMutateArchiveProcedurePdf } from "@/service/ArchiveMutations";
import { useFetchArchivedProcedures } from "@/service/ArchiveQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


type Props = {
	title?: React.ReactNode;
	subtitle?: React.ReactNode;
	options: ArchivedProjectsRequest;
	initialRowsPerPage?: number;
	containerHeight?: number;
	/** On procedure page: hide Procedure column, show Completed by */
	embeddedInProcedurePage?: boolean;
	/** When provided (e.g. procedure page), use this data instead of fetching — single source of truth */
	archivedProceduresFromParent?: ArchivedProjectsResponse | null;
	isLoadingFromParent?: boolean;
	/** Client-side search query to filter by procedure name or description */
	searchQuery?: string;
};

function matchesSearch(proc: ArchiveProject, query: string): boolean {
	if (!query.trim()) return true;
	const q = query.trim().toLowerCase();
	const name = (proc.Name ?? "").toLowerCase();
	const desc = (proc.Description ?? "").toLowerCase();
	return name.includes(q) || desc.includes(q);
}

const HistoryTable: FC<Props> = ({
	title,
	subtitle,
	options,
	initialRowsPerPage,
	containerHeight,
	embeddedInProcedurePage = false,
	archivedProceduresFromParent,
	isLoadingFromParent,
	searchQuery = "",
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage || 5);
	const [page, setPage] = useState(0);

	const [open, setOpen] = useState(false);
	const [selectedItem, setSelectedItem] = useState<ArchiveProject | null>(null);

	const [generatingPdfId, setGeneratingPdfId] = useState<number | null>(null);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const isFixedHeight = Boolean(containerHeight) && !isMobile;

	const hasParentData = archivedProceduresFromParent !== undefined;
	const query = useFetchArchivedProcedures(options, {
		enabled: !hasParentData,
	});

	const archivedProcedures = hasParentData
		? archivedProceduresFromParent
		: query.data;
	const tableLoading = hasParentData
		? (isLoadingFromParent ?? false)
		: query.isLoading;
	const isFetching = hasParentData ? false : query.isFetching;

	const createArchivedProcedurePdf = useMutateArchiveProcedurePdf();

	// Reset to first page when filters or search change
	useEffect(() => {
		setPage(0);
	}, [options.StartDate, options.EndDate, options.SelectedUser, searchQuery]);

	const handleClickOpen = (item: ArchiveProject) => {
		setSelectedItem(item);
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
		setSelectedItem(null);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const filteredProcedures = useMemo(() => {
		let list = archivedProcedures?.ArchivedProjects;
		if (!list) return undefined;

		if (options.SelectedUser !== 0) {
			list = list.filter((proc) =>
				proc.Users?.some((user) => user.UserId === options.SelectedUser),
			);
		}

		if (searchQuery.trim()) {
			list = list.filter((proc) => matchesSearch(proc, searchQuery));
		}

		return list;
	}, [archivedProcedures?.ArchivedProjects, options.SelectedUser, searchQuery]);

	const visibleRows = filteredProcedures?.slice(
		page * rowsPerPage,
		page * rowsPerPage + rowsPerPage,
	);

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleCreatePdf = async (procedureId: number) => {
		if (generatingPdfId !== null) return;
		setGeneratingPdfId(procedureId);
		try {
			await createArchivedProcedurePdf(procedureId);
		} catch (error) {
			console.error(error);
		} finally {
			setGeneratingPdfId(null);
		}
	};

	const surfaceSx = {
		border: getSurfaceBorderStyle(theme, "strong"),
		overflow: "hidden",
		boxShadow:
			theme.palette.mode === "dark"
				? `0 1px 0 ${alpha(theme.palette.common.white, 0.03)}`
				: `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}`,
	};

	const softRowHoverSx = {
		transition: "background-color 0.15s ease",
		"&:hover": {
			bgcolor: alpha(
				theme.palette.primary.main,
				theme.palette.mode === "dark" ? 0.08 : 0.04,
			),
		},
	};

	const headerCellSx = {
		fontWeight: 650,
		color: "text.secondary",
		py: 1.25,
		px: 2,
		bgcolor:
			theme.palette.mode === "dark"
				? alpha(theme.palette.background.default, 0.55)
				: alpha(theme.palette.background.default, 0.98),
		borderBottom: getSurfaceBorderStyle(theme, "subtle"),
	};

	const bodyCellSx = {
		py: 1.35,
		px: 2,
		borderBottom: getSurfaceBorderStyle(theme, "subtle"),
	};
	const tableMinWidth = embeddedInProcedurePage ? 0 : 760;

	const softIconButtonSx = {
		borderRadius: 1.5,
		border: `1px solid ${getSurfaceBorderColor(theme, "subtle")}`,
		backgroundColor: theme.palette.background.paper,
		"&:hover": {
			bgcolor: alpha(
				theme.palette.primary.main,
				theme.palette.mode === "dark" ? 0.12 : 0.05,
			),
			borderColor: alpha(theme.palette.primary.main, 0.18),
		},
	};

	const renderActions = (row: ArchiveProject) => (
		<Stack
			direction="row"
			spacing={0.25}
			alignItems="center"
			justifyContent="flex-end"
			sx={{
				px: 0.4,
				py: 0.3,
				borderRadius: 2,
				bgcolor: alpha(
					theme.palette.background.default,
					theme.palette.mode === "dark" ? 0.5 : 0.9,
				),
				border: `1px solid ${getSurfaceBorderColor(theme, "subtle")}`,
				minWidth: 104,
				maxWidth: "100%",
				overflow: "hidden",
			}}
		>
			<Tooltip
				title={<Translate String="Archive.History.Action.View">View</Translate>}
				arrow
			>
				<IconButton
					size="small"
					onClick={() => handleClickOpen(row)}
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Archive.Ui.Components.History.HistoryTable.L246C17.ViewCompletedProcedure", "View completed procedure")}
					sx={softIconButtonSx}
				>
					<VisibilityIcon fontSize="small" />
				</IconButton>
			</Tooltip>

			<Tooltip
				title={
					<Translate String="Archive.History.Action.Export">Export</Translate>
				}
				arrow
			>
				<IconButton
					size="small"
					component={Link}
					to={`/archive/procedure/${row.Id}/data`}
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Archive.Ui.Components.History.HistoryTable.L263C17.SeeAllData", "See all data")}
					sx={softIconButtonSx}
				>
					<TableChartIcon fontSize="small" />
				</IconButton>
			</Tooltip>

			{row.FileCreated ? (
				<PdfMenu row={row} pdfColor={theme.palette.error.main} />
			) : (
				<Tooltip
					arrow
					title={
						generatingPdfId === row.Id ? (
							<Translate String="Archive.History.GeneratingPdf">
								Generating PDF…
							</Translate>
						) : (
							<Translate String="Archive.History.CreatePdf">
								Create PDF
							</Translate>
						)
					}
				>
					<span>
						<IconButton
							size="small"
							onClick={() => {
								void handleCreatePdf(row.Id);
							}}
							disabled={generatingPdfId !== null}
							aria-label="Create PDF"
							sx={softIconButtonSx}
						>
							{generatingPdfId === row.Id ? (
								<CircularProgress size={16} color="inherit" />
							) : (
								<ReplayIcon fontSize="small" />
							)}
						</IconButton>
					</span>
				</Tooltip>
			)}
		</Stack>
	);

	const headerTitle = embeddedInProcedurePage ? (
		<Translate String="Archive.History.Title">History</Translate>
	) : (
		<Translate String="Archive.History.Title">Procedure history</Translate>
	);
	const effectiveHeaderTitle = title ?? headerTitle;
	const effectiveSubtitle = subtitle ?? (
		<Typography variant="caption" color="text.secondary">
			<Translate String="Archive.History.Subtitle">
				Browse completions in the selected range
			</Translate>
		</Typography>
	);

	const totalCount = filteredProcedures?.length || 0;

	const EmptyState = (
		<Box sx={{ py: 6, textAlign: "center" }}>
			<Typography variant="body2" color="text.secondary">
				<Translate String="Archive.History.NoResults">
					No history found for this period.
				</Translate>
			</Typography>
		</Box>
	);

	return (
		<>
			{/* subtle top loading bar when refetching */}
			{isFetching && !tableLoading && (
				<LinearProgress color="primary" sx={{ height: 2, borderRadius: 999 }} />
			)}

			{isMobile ? (
				<Card variant="outlined" sx={surfaceSx}>
					<Box
						sx={{
							px: 2,
							py: 1.5,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 1,
							bgcolor:
								theme.palette.mode === "dark"
									? alpha(theme.palette.background.default, 0.55)
									: alpha(theme.palette.background.default, 0.95),
							borderBottom: "1px solid",
							borderBottomColor: "divider",
						}}
					>
						<Stack spacing={0.25} sx={{ minWidth: 0 }}>
							<Typography
								variant="subtitle1"
								sx={{ fontWeight: 700, letterSpacing: -0.2 }}
							>
								{effectiveHeaderTitle}
							</Typography>
							<Box
								sx={{ display: "flex", alignItems: "center", minHeight: 18 }}
							>
								{effectiveSubtitle}
							</Box>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ mt: 0.25 }}
							>
								{totalCount} <Translate String="Auto.Features.Archive.Ui.Components.History.HistoryTable.L377C21.Result">result</Translate>{totalCount === 1 ? "" : "s"}
							</Typography>
						</Stack>

						<Chip
							size="small"
							label={
								<Typography variant="caption" sx={{ fontWeight: 650 }}>
									{page + 1}/{Math.max(1, Math.ceil(totalCount / rowsPerPage))}
								</Typography>
							}
							sx={{
								borderRadius: 2,
								bgcolor: alpha(theme.palette.primary.main, 0.08),
								color: "primary.main",
								"& .MuiChip-label": { px: 1 },
							}}
						/>
					</Box>

					<CardContent sx={{ px: 1.5, py: 1.5, "&:last-child": { pb: 1.5 } }}>
						{!tableLoading &&
							(!visibleRows || visibleRows.length === 0) &&
							EmptyState}

						{!tableLoading && visibleRows && visibleRows.length > 0 && (
							<Stack spacing={1.25}>
								{visibleRows.map((row) => {
									const completedBy = row.Users?.length
										? row.Users.map((u) => u.UserName).join(", ")
										: "—";
									const date = dayjs(row.Created).format("DD/MM/YY");

									return (
							<Card
								key={row.Id}
								variant="outlined"
								sx={{
									borderRadius: 2.5,
									borderColor: getSurfaceBorderColor(theme, "subtle"),
									transition:
										"border-color 0.15s, background-color 0.15s, transform 0.15s",
												"&:hover": {
													borderColor: alpha(theme.palette.primary.main, 0.25),
													backgroundColor: alpha(
														theme.palette.primary.main,
														theme.palette.mode === "dark" ? 0.05 : 0.025,
													),
													transform: "translateY(-1px)",
												},
											}}
										>
											<CardContent
												sx={{ px: 1.5, py: 1.25, "&:last-child": { pb: 1.25 } }}
											>
												<Stack spacing={0.75}>
													{!embeddedInProcedurePage ? (
														<Box sx={{ minWidth: 0 }}>
															<Typography
																component={Link}
																to={`/archive/procedure/${row.ParentId}`}
																variant="subtitle2"
																sx={{
																	display: "block",
																	textDecoration: "none",
																	color: "text.primary",
																	fontWeight: 700,
																	letterSpacing: -0.15,
																	overflow: "hidden",
																	textOverflow: "ellipsis",
																	whiteSpace: "nowrap",
																	"&:hover": { color: "primary.main" },
																}}
															>
																{row.Name}
															</Typography>
															{row.Description ? (
																<Typography
																	variant="body2"
																	color="text.secondary"
																	sx={{
																		mt: 0.25,
																		display: "-webkit-box",
																		WebkitLineClamp: 2,
																		WebkitBoxOrient: "vertical",
																		overflow: "hidden",
																		lineHeight: 1.5,
																	}}
																>
																	{row.Description}
																</Typography>
															) : null}
														</Box>
													) : (
														<Box sx={{ minWidth: 0 }}>
															<Typography
																variant="body2"
																color="text.secondary"
																sx={{
																	display: "-webkit-box",
																	WebkitLineClamp: 2,
																	WebkitBoxOrient: "vertical",
																	overflow: "hidden",
																	lineHeight: 1.5,
																}}
															>
																{row.Description || "—"}
															</Typography>
														</Box>
													)}

													<Stack
														direction="row"
														flexWrap="wrap"
														gap={0.75}
														alignItems="center"
													>
														<Chip
															size="small"
															label={
																<Typography
																	variant="caption"
																	sx={{ fontWeight: 650 }}
																>
																	{date}
																</Typography>
															}
															sx={{
																borderRadius: 2,
																bgcolor: alpha(theme.palette.primary.main, 0.08),
																color: "primary.main",
																"& .MuiChip-label": { px: 1 },
															}}
														/>

														<Chip
															size="small"
															label={
																<Typography
																	variant="caption"
																	sx={{ fontWeight: 650 }}
																>
																	{completedBy}
																</Typography>
															}
															sx={{
																borderRadius: 2,
																bgcolor: alpha(
																	theme.palette.text.primary,
																	theme.palette.mode === "dark" ? 0.08 : 0.04,
																),
																border: `1px solid ${getSurfaceBorderColor(theme, "subtle")}`,
																maxWidth: "100%",
																"& .MuiChip-label": {
																	px: 1,
																	overflow: "hidden",
																	textOverflow: "ellipsis",
																},
															}}
														/>

														{!embeddedInProcedurePage && (
															<Chip
																size="small"
																label={
																	<Typography
																		variant="caption"
																		sx={{ fontWeight: 700 }}
																	>
																		<Translate String="Archive.History.Status.Completed">
																			Completed
																		</Translate>
																	</Typography>
																}
																sx={{
																	borderRadius: 2,
																	bgcolor: alpha(
																		theme.palette.success.main,
																		0.12,
																	),
																	color: theme.palette.success.main,
																	"& .MuiChip-label": { px: 1 },
																}}
															/>
														)}
													</Stack>

													<Divider
														sx={{
															borderColor: alpha(
																theme.palette.text.primary,
																0.06,
															),
														}}
													/>

													<Stack direction="row" justifyContent="flex-end">
														{renderActions(row)}
													</Stack>
												</Stack>
											</CardContent>
										</Card>
									);
								})}
							</Stack>
						)}
					</CardContent>

					{totalCount > 0 && (
						<TablePagination
							rowsPerPageOptions={[5, 10, 25]}
							component="div"
							count={totalCount}
							rowsPerPage={rowsPerPage}
							page={page}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
							showFirstButton={false}
							showLastButton={false}
							sx={{
								borderTop: getSurfaceBorderStyle(theme, "subtle"),
								"& .MuiTablePagination-toolbar": { px: 1.5, minHeight: 52 },
								"& .MuiTablePagination-selectLabel": { display: "none" },
								"& .MuiTablePagination-displayedRows": {
									fontSize: "0.82rem",
									color: "text.secondary",
								},
								"& .MuiTablePagination-actions .MuiIconButton-root": {
									color: "text.secondary",
								},
							}}
						/>
					)}
				</Card>
			) : (
				<Card
					variant="outlined"
					sx={{
						...surfaceSx,
						...(isFixedHeight
							? {
									height: containerHeight,
									display: "flex",
									flexDirection: "column",
								}
							: {}),
					}}
				>
					<Box
						sx={{
							px: 2,
							py: 1.5,
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 2,
							bgcolor:
								theme.palette.mode === "dark"
									? alpha(theme.palette.background.default, 0.55)
									: alpha(theme.palette.background.default, 0.95),
							borderBottom: getSurfaceBorderStyle(theme, "subtle"),
						}}
					>
						<Stack spacing={0.25} sx={{ minWidth: 0 }}>
							<Typography
								variant="subtitle1"
								sx={{ fontWeight: 700, letterSpacing: -0.2 }}
							>
								{effectiveHeaderTitle}
							</Typography>
							<Box
								sx={{ display: "flex", alignItems: "center", minHeight: 18 }}
							>
								{effectiveSubtitle}
							</Box>
							<Typography variant="caption" color="text.secondary">
								{totalCount} <Translate String="Auto.Features.Archive.Ui.Components.History.HistoryTable.L653C21.Result">result</Translate>{totalCount === 1 ? "" : "s"}
							</Typography>
						</Stack>

						{!embeddedInProcedurePage && (
							<Chip
								size="small"
								label={
									<Typography variant="caption" sx={{ fontWeight: 700 }}>
										<Translate String="Archive.History.Status.Completed">
											Completed
										</Translate>
									</Typography>
								}
								sx={{
									borderRadius: 2,
									bgcolor: alpha(theme.palette.success.main, 0.12),
									color: theme.palette.success.main,
									"& .MuiChip-label": { px: 1.25 },
								}}
							/>
						)}
					</Box>

					<TableContainer
						sx={{
							overflowX: embeddedInProcedurePage ? "hidden" : "auto",
							overflowY: "auto",
							position: "relative",
							WebkitOverflowScrolling: "touch",
							flex: isFixedHeight ? 1 : undefined,
							minHeight: isFixedHeight ? 0 : undefined,
						}}
					>
						<Table
							size="small"
							stickyHeader={isFixedHeight}
							sx={{ minWidth: tableMinWidth, width: "100%" }}
						>
							<TableHead>
								<TableRow>
									{!embeddedInProcedurePage ? (
										<TableCell sx={headerCellSx}>
											<Translate String="Archive.History.Table.Header.Procedure">
												Procedure
											</Translate>
										</TableCell>
									) : (
										<TableCell sx={headerCellSx}>
											<Translate String="Archive.History.Table.Header.Date">
												Description
											</Translate>
										</TableCell>
									)}

									<TableCell sx={headerCellSx}>
										<Translate String="Archive.History.Table.Header.Date">
											Date
										</Translate>
									</TableCell>

									<TableCell sx={headerCellSx}>
										<Translate String="Archive.History.Table.Header.CompletedBy">
											Completed by
										</Translate>
									</TableCell>

									{!embeddedInProcedurePage && (
										<TableCell sx={headerCellSx}>
											<Translate String="Archive.History.Table.Header.Status">
												Status
											</Translate>
										</TableCell>
									)}

									<TableCell sx={{ ...headerCellSx, textAlign: "right" }}>
										<Translate String="Archive.History.Table.Header.Actions">
											Actions
										</Translate>
									</TableCell>
								</TableRow>
							</TableHead>

							<TableBody>
								{!tableLoading &&
									(!visibleRows || visibleRows.length === 0) && (
										<TableRow>
											<TableCell
												colSpan={embeddedInProcedurePage ? 4 : 5}
												align="center"
												sx={{ py: 6 }}
											>
												<Typography variant="body2" color="text.secondary">
													<Translate String="Archive.History.NoResults">
														No history found for this period.
													</Translate>
												</Typography>
											</TableCell>
										</TableRow>
									)}

								{!tableLoading &&
									visibleRows?.map((row) => {
										const completedBy = row.Users?.length
											? row.Users.map((u) => u.UserName).join(", ")
											: "—";

										return (
											<TableRow key={row.Id} sx={softRowHoverSx}>
												{!embeddedInProcedurePage ? (
													<TableCell
														sx={{ ...bodyCellSx, minWidth: 260, maxWidth: 520 }}
													>
														<Tooltip title={row.Name}>
															<Box sx={{ minWidth: 0 }}>
																<Typography
																	component={Link}
																	to={`/archive/procedure/${row.ParentId}`}
																	variant="body2"
																	sx={{
																		display: "inline-block",
																		textDecoration: "none",
																		color: "text.primary",
																		fontWeight: 650,
																		letterSpacing: -0.1,
																		maxWidth: "100%",
																		overflow: "hidden",
																		textOverflow: "ellipsis",
																		whiteSpace: "nowrap",
																		"&:hover": { color: "primary.main" },
																	}}
																>
																	{row.Name}
																</Typography>

																{row.Description ? (
																	<Typography
																		variant="caption"
																		color="text.secondary"
																		sx={{
																			display: "block",
																			mt: 0.25,
																			maxWidth: "100%",
																			overflow: "hidden",
																			textOverflow: "ellipsis",
																			whiteSpace: "nowrap",
																		}}
																	>
																		{row.Description}
																	</Typography>
																) : null}
															</Box>
														</Tooltip>
													</TableCell>
												) : (
													<TableCell
														sx={{
															...bodyCellSx,
															minWidth: 0,
															maxWidth: "none",
															whiteSpace: "normal",
															wordBreak: "break-word",
														}}
													>
														<Typography variant="body2">
															{row.Description || "—"}
														</Typography>
													</TableCell>
												)}

												<TableCell
													sx={{
														...bodyCellSx,
														whiteSpace: "nowrap",
														width: 130,
													}}
												>
													<Typography variant="body2">
														{dayjs(row.Created).format("DD/MM/YY")}
													</Typography>
												</TableCell>

												<TableCell
													sx={{
														...bodyCellSx,
														minWidth: 0,
														maxWidth: "none",
														whiteSpace: "normal",
														wordBreak: "break-word",
													}}
												>
													<Typography variant="body2">{completedBy}</Typography>
												</TableCell>

												{!embeddedInProcedurePage && (
													<TableCell sx={{ ...bodyCellSx, width: 120 }}>
														<Chip
															size="small"
															label={
																<Typography
																	variant="caption"
																	sx={{ fontWeight: 700 }}
																>
																	<Translate String="Archive.History.Status.Completed">
																		Completed
																	</Translate>
																</Typography>
															}
															sx={{
																borderRadius: 2,
																bgcolor: alpha(
																	theme.palette.success.main,
																	0.12,
																),
																color: theme.palette.success.main,
																"& .MuiChip-label": { px: 1.1 },
															}}
														/>
													</TableCell>
												)}

												<TableCell
													sx={{ ...bodyCellSx, textAlign: "right", width: 180 }}
												>
													<Box
														sx={{ display: "flex", justifyContent: "flex-end" }}
													>
														{renderActions(row)}
													</Box>
												</TableCell>
											</TableRow>
										);
									})}
							</TableBody>
						</Table>
					</TableContainer>

					{totalCount > 0 && (
						<TablePagination
							rowsPerPageOptions={[5, 10, 25]}
							component="div"
							count={totalCount}
							rowsPerPage={rowsPerPage}
							page={page}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
							showFirstButton
							showLastButton
							sx={{
								borderTop: getSurfaceBorderStyle(theme, "subtle"),
								"& .MuiTablePagination-toolbar": { px: 2, minHeight: 56 },
								"& .MuiTablePagination-displayedRows": {
									color: "text.secondary",
								},
								"& .MuiTablePagination-selectLabel": {
									color: "text.secondary",
								},
								"& .MuiTablePagination-actions .MuiIconButton-root": {
									color: "text.secondary",
								},
								mt: isFixedHeight ? "auto" : undefined,
							}}
						/>
					)}
				</Card>
			)}

			{selectedItem && (
				<StepModal
					open={open}
					handleClose={handleClose}
					selectedItem={selectedItem}
				/>
			)}
		</>
	);
};

export default HistoryTable;
